import apiService from './apiService';
import { ROLES } from '../constants/roles';

// Create or update user profile (still needs direct Firestore for initial creation)
export const createUserProfile = async (user, additionalData = {}) => {
  try {
    // This still needs direct Firestore access for initial user creation
    // In a full implementation, this would be handled by a Firebase Auth trigger
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const userRef = doc(db, 'users', user.uid);
    const userProfile = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      role: ROLES.USER, // Default role
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      ...additionalData
    };

    await setDoc(userRef, userProfile);
    return userProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

// Get current user profile and permissions from API
export const getCurrentUserProfile = async () => {
  try {
    return await apiService.getCurrentUserProfile();
  } catch (error) {
    console.error('Error getting current user profile:', error);
    throw error;
  }
};

// Get user profile (fallback to direct Firestore for backward compatibility)
export const getUserProfile = async (uid) => {
  try {
    // For now, fallback to direct Firestore access
    // In production, this should use the API
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

// Update user profile via API
export const updateUserProfile = async (uid, updates) => {
  try {
    return await apiService.updateUserProfile(uid, updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Update user role via API (admin only)
export const updateUserRole = async (uid, newRole, updatedBy) => {
  try {
    return await apiService.updateUserRole(uid, newRole);
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

// Get all users via API (admin only)
export const getAllUsers = async () => {
  try {
    const response = await apiService.getAllUsers();
    return response.users;
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

// Get users by role (fallback to direct Firestore)
export const getUsersByRole = async (role) => {
  try {
    // For now, fallback to direct Firestore access
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', role), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users by role:', error);
    throw error;
  }
};

// Deactivate user via API (admin only)
export const deactivateUser = async (uid, deactivatedBy) => {
  try {
    return await apiService.deactivateUser(uid);
  } catch (error) {
    console.error('Error deactivating user:', error);
    throw error;
  }
};

// Delete user via API (super admin only)
export const deleteUser = async (uid) => {
  try {
    return await apiService.deleteUser(uid);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Update last login (fallback to direct Firestore)
export const updateLastLogin = async (uid) => {
  try {
    // For now, fallback to direct Firestore access
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
    // Don't throw error for this as it's not critical
  }
};

 